import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Property, PropertyFilters, PaginatedResponse } from '../../types';
import * as propertyService from '../../services/propertyService';

interface PropertiesState {
  properties: Property[];
  propertiesList: Property[];
  currentProperty: Property | null;
  filters: PropertyFilters;
  pagination: {
    page: number;
    limit: number;
    totalPages: number;
    totalCount: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: string | null;
}

const initialState: PropertiesState = {
  properties: [],
  propertiesList: [],
  currentProperty: null,
  filters: {
    page: 1,
    limit: 16,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  },
  pagination: {
    page: 1,
    limit: 16,
    totalPages: 0,
    totalCount: 0,
    hasNext: false,
    hasPrev: false,
  },
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
};

export const fetchProperties = createAsyncThunk(
  'properties/fetchProperties',
  async (filters: PropertyFilters = {}, { rejectWithValue }) => {
    try {
      const response = await propertyService.getProperties(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch properties');
    }
  }
);
export const fetchPropertiesList = createAsyncThunk(
  'properties/fetchPropertiesList',
  async (filters: PropertyFilters = {}, { rejectWithValue }) => {
    try {
      const response = await propertyService.getPropertiesList(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch properties');
    }
  }
);

export const fetchProperty = createAsyncThunk(
  'properties/fetchProperty',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await propertyService.getProperty(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch property');
    }
  }
);

export const createProperty = createAsyncThunk(
  'properties/createProperty',
  async (propertyData: any, { rejectWithValue }) => {
    try {
      const response = await propertyService.createProperty(propertyData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create property');
    }
  }
);

export const bulkImportProperties = createAsyncThunk(
  'properties/bulkImportProperties',
  async (properties: any[], { rejectWithValue }) => {
    try {
      const response = await propertyService.bulkImportProperties(properties);
      // Return the full response object which includes count, message, partial, errors
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to bulk import properties');
    }
  }
);

export const updateProperty = createAsyncThunk(
  'properties/updateProperty',
  async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
    try {
      const response = await propertyService.updateProperty(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update property');
    }
  }
);

export const deleteProperty = createAsyncThunk(
  'properties/deleteProperty',
  async (id: string, { rejectWithValue }) => {
    try {
      await propertyService.deleteProperty(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete property');
    }
  }
);

export const searchProperties = createAsyncThunk(
  'properties/searchProperties',
  async ({ query, filters = {} }: { query: string; filters?: PropertyFilters }, { rejectWithValue }) => {
    try {
      const response = await propertyService.searchProperties(query, filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to search properties');
    }
  }
);

export const fetchUserProperties = createAsyncThunk(
  'properties/fetchUserProperties',
  async (_, { rejectWithValue }) => {
    try {
      const response = await propertyService.getUserProperties();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch user properties');
    }
  }
);

// Property Transfer Thunks
export const getCompaniesByAdminEmail = createAsyncThunk(
  'properties/getCompaniesByAdminEmail',
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await propertyService.getCompaniesByAdminEmail(email);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to fetch companies');
    }
  }
);

export const getPreTransferSummary = createAsyncThunk(
  'properties/getPreTransferSummary',
  async ({ propertyId, transferOptions }: {
    propertyId: string;
    transferOptions: {
      includeMaintenanceRequests: boolean;
      includeInvoices: boolean;
      includeInactiveTenants: boolean;
    };
  }, { rejectWithValue }) => {
    try {
      const response = await propertyService.getPreTransferSummary(propertyId, transferOptions);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to fetch transfer summary');
    }
  }
);

export const schedulePropertyTransfer = createAsyncThunk(
  'properties/schedulePropertyTransfer',
  async (data: {
    propertyId: string;
    targetAdminEmail: string;
    targetCompanyId: string | undefined;
    scheduledDate?: string;
    reason?: string;
    transferNotes?: string;
    transferOptions: {
      includeMaintenanceRequests: boolean;
      includeInvoices: boolean;
      includeInactiveTenants: boolean;
    };
  }, { rejectWithValue }) => {
    try {
      const response = await propertyService.schedulePropertyTransfer(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to schedule transfer');
    }
  }
);

export const cancelPropertyTransfer = createAsyncThunk(
  'properties/cancelPropertyTransfer',
  async ({ transferId, propertyId, cancellationReason }: {
    transferId: string;
    propertyId: string;
    cancellationReason?: string;
  }, { rejectWithValue }) => {
    try {
      const response = await propertyService.cancelPropertyTransfer(transferId, propertyId, cancellationReason);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to cancel transfer');
    }
  }
);

export const getScheduledTransfers = createAsyncThunk(
  'properties/getScheduledTransfers',
  async (propertyId: string, { rejectWithValue }) => {
    try {
      const response = await propertyService.getScheduledTransfers(propertyId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to fetch scheduled transfers');
    }
  }
);

const propertiesSlice = createSlice({
  name: 'properties',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<PropertyFilters>) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
        // Ensure limit is never undefined or 0
        limit: action.payload.limit || state.filters.limit || 6
      };
    },
    clearFilters: (state) => {
      state.filters = {
        page: 1,
        limit: 6,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      };
    },
    setCurrentProperty: (state, action: PayloadAction<Property | null>) => {
      state.currentProperty = action.payload;
    },
    clearProperties: (state) => {
      state.properties = [];
      state.currentProperty = null;
      state.pagination = initialState.pagination;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Properties
      .addCase(fetchProperties.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProperties.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.properties = action.payload.data;
          state.pagination = action.payload.pagination;
          state.error = null;
        }
      })
      .addCase(fetchProperties.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Failed to fetch properties';
      })
      // Fetch Property
      .addCase(fetchProperty.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProperty.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.currentProperty = action.payload;
          state.error = null;
        }
      })
      .addCase(fetchProperty.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Failed to fetch property';
      })
      // Create Property
      .addCase(createProperty.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createProperty.fulfilled, (state, action) => {
        state.isCreating = false;
        if (action.payload) {
          state.properties.unshift(action.payload);
          state.error = null;
        }
      })
      .addCase(createProperty.rejected, (state, action) => {
        state.isCreating = false;
        state.error = (action.payload as string) || 'Failed to create property';
      })
      // Update Property
      .addCase(updateProperty.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateProperty.fulfilled, (state, action) => {
        state.isUpdating = false;
        if (action.payload) {
          const updatedProperty = action.payload;
          const index = state.properties.findIndex(p => p._id === updatedProperty._id);
          if (index !== -1) {
            state.properties[index] = updatedProperty;
          }
          if (state.currentProperty?._id === updatedProperty._id) {
            state.currentProperty = updatedProperty;
          }
          state.error = null;
        }
      })
      .addCase(updateProperty.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = (action.payload as string) || 'Failed to update property';
      })
      // Delete Property
      .addCase(deleteProperty.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteProperty.fulfilled, (state, action) => {
        state.isDeleting = false;
        if (action.payload) {
          state.properties = state.properties.filter(p => p._id !== action.payload);
          if (state.currentProperty?._id === action.payload) {
            state.currentProperty = null;
          }
          state.error = null;
        }
      })
      .addCase(deleteProperty.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = (action.payload as string) || 'Failed to delete property';
      })
      // Search Properties
      .addCase(searchProperties.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchProperties.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.properties = action.payload.data;
          state.pagination = action.payload.pagination;
          state.error = null;
        }
      })
      .addCase(searchProperties.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Failed to search properties';
      })
      // Fetch User Properties
      .addCase(fetchUserProperties.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserProperties.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.properties = action.payload.data;
          state.pagination = action.payload.pagination;
          state.error = null;
        }
      })
      .addCase(fetchUserProperties.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Failed to fetch user properties';
      })
      .addCase(fetchPropertiesList.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPropertiesList.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          let list = action.payload.data || [];
          list.sort((a, b) => a.name.localeCompare(b.name));
          state.propertiesList = list;
          state.error = null;
        }
      })
      .addCase(fetchPropertiesList.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Failed to fetch properties';
      })
      ;
  },
});

export const {
  clearError,
  setFilters,
  clearFilters,
  setCurrentProperty,
  clearProperties,
} = propertiesSlice.actions;

export default propertiesSlice.reducer;